import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Boxes, RefreshCw } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { CatalogBody } from "../components/modules/ModuleCatalog";
import { refusal, type NewRun, type Refusal, type Startable } from "../components/modules/moduleFlow";
import { RunsBody } from "../components/modules/RunsList";
import { RefusalNotice, StartForm } from "../components/modules/StartForm";
import { apiGet, apiSend } from "../lib/http";
import { useMe } from "../lib/me";

/** /modules: what this environment runs, a start form for each runnable module, and recent runs. */
export function Modules() {
  const { me } = useMe();
  const navigate = useNavigate();
  const client = useQueryClient();
  const [starting, setStarting] = useState<Startable | null>(null);
  const [refused, setRefused] = useState<Refusal | null>(null);

  const catalog = useQuery({ queryKey: ["modules", "catalog"], queryFn: () => apiGet("/api/v1/modules/catalog") });
  const runs = useQuery({ queryKey: ["modules", "runs"], queryFn: () => apiGet("/api/v1/modules/runs") });
  // The same query and key as the P&ID screen's package list, so one fetch serves both.
  const listed = useQuery({ queryKey: ["documents", "packages"], queryFn: () => apiGet("/api/v1/documents/packages") });
  const start = useMutation({
    mutationFn: ({ requestType, body }: { requestType: Startable; body: NewRun }) =>
      apiSend("/api/v1/modules/{request_type}/runs", body, { path: { request_type: requestType } }),
    onMutate: () => setRefused(null),
    onSuccess: (run) => {
      void client.invalidateQueries({ queryKey: ["modules", "runs"] });
      navigate(`/modules/runs/${run.id}`);
    },
    onError: (e) => setRefused(refusal(e)),
  });

  const open = (requestType: Startable) => {
    setRefused(null);
    setStarting(requestType);
  };

  return (
    <div className="page stack" style={{ gap: 24 }}>
      <div>
        <h2 style={{ fontSize: 26, marginBottom: 6 }}><span className="row"><Boxes size={22} aria-hidden="true" /> Modules</span></h2>
        <p className="muted">What this environment runs, why a module is off, and the runs in your packages.</p>
      </div>

      {starting && (
        <div className="stack">
          {refused && <RefusalNotice refusal={refused} />}
          <StartForm
            key={starting}
            requestType={starting}
            me={me}
            packages={(listed.data ?? []).map((p) => p.name)}
            packagesLoading={listed.isPending}
            busy={start.isPending}
            onSubmit={(requestType, body) => start.mutate({ requestType, body })}
            onCancel={() => { setStarting(null); setRefused(null); }}
          />
        </div>
      )}

      <section>
        <div className="section-title"><h3>Catalog</h3></div>
        <CatalogBody loading={catalog.isPending} error={catalog.error} data={catalog.data} me={me} onStart={open} />
      </section>

      <section>
        <div className="section-title" style={{ justifyContent: "space-between" }}>
          <h3>Recent runs</h3>
          <button className="ghost" onClick={() => void runs.refetch()} disabled={runs.isFetching} aria-label="Refresh runs">
            <span className="row" style={{ gap: 6 }}><RefreshCw size={14} aria-hidden="true" className={runs.isFetching ? "spin" : undefined} />Refresh</span>
          </button>
        </div>
        <RunsBody loading={runs.isPending} error={runs.error} data={runs.data} />
      </section>

      <p className="muted" style={{ fontSize: 14 }}>
        A module that is off says why: in preview until someone trusts it, or blocked on data that has not
        arrived. A run that reaches a gate waits for an engineer; nothing is bought past a gate without one.
      </p>
    </div>
  );
}
